import {
  PRIORITY_LOW,
  mapApiPriorityToCode,
  mapPriorityCodeToApiValue,
} from '@/views/external-order-request/helpers/priority'
import orderTypes from '@/dictionaries/orderTypes'
import moment from 'moment'

const DEFAULT_ORDER_REQUEST_FORM = {
  customerId: null,
  requestedType: '',
  eventKind: null,
  requestedStartAt: '',
  requestedEndAt: '',
  pickupAddress: null,
  deliveryAddress: null,
  patient: {
    surname: '',
    name: '',
    pesel: '',
    age: null,
    weight: null,
    sex: 'M',
  },
  notes: '',
  priority: PRIORITY_LOW,
  returnTransport: false,
  infectious: false,
}

const createEmptyOrderRequestForm = () => ({
  ...DEFAULT_ORDER_REQUEST_FORM,
  patient: { ...DEFAULT_ORDER_REQUEST_FORM.patient },
})

const isMedicalCourierType = requestedType => requestedType === orderTypes.medical_courier
const isEventType = requestedType => requestedType === orderTypes.event
const requiresPatient = requestedType => !isMedicalCourierType(requestedType) && !isEventType(requestedType)
const requiresPickup = requestedType => !isEventType(requestedType)

const toLocalDatetimeInputValue = value => {
  if (!value) {
    return ''
  }

  const parsed = moment(value)
  if (!parsed.isValid()) {
    return ''
  }

  return parsed.format('YYYY-MM-DD HH:mm')
}

const toApiDatetimeValue = value => {
  if (!value) {
    return null
  }

  const parsed = moment(value, ['YYYY-MM-DD HH:mm', moment.ISO_8601], true)
  if (!parsed.isValid()) {
    return null
  }

  return parsed.toISOString()
}

const normalizePositiveInteger = value => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
    return value
  }

  if (typeof value === 'string' && /^\d+$/.test(value) && Number(value) > 0) {
    return Number(value)
  }

  return null
}

const normalizeAddressValue = payload => {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  return {
    id: normalizePositiveInteger(payload.id),
    name: payload.name || '',
    alias: payload.alias || '',
    address: payload.address || '',
    zip: payload.zip || '',
    city: payload.city || '',
  }
}

const mapOrderRequestToForm = payload => {
  const priorityCode = mapApiPriorityToCode(payload?.priority)
  const patientPayload = payload?.patient || {}

  return {
    customerId: payload?.customerId || null,
    requestedType: payload?.requestedType || '',
    eventKind: payload?.eventKind || null,
    requestedStartAt: toLocalDatetimeInputValue(payload?.requestedStartAt),
    requestedEndAt: toLocalDatetimeInputValue(payload?.requestedEndAt),
    pickupAddress: normalizeAddressValue(payload?.pickup),
    deliveryAddress: normalizeAddressValue(payload?.delivery),
    patient: {
      surname: patientPayload?.surname || '',
      name: patientPayload?.name || '',
      pesel: patientPayload?.pesel || '',
      age: patientPayload?.age || null,
      weight: patientPayload?.weight || null,
      sex: patientPayload?.sex || null,
    },
    notes: payload?.notes || '',
    priority: priorityCode || PRIORITY_LOW,
    returnTransport: !!payload?.returnTransport,
    infectious: !!payload?.infectious,
  }
}

const normalizeAddressForPayload = value => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  if (typeof value === 'number') {
    return { id: normalizePositiveInteger(value) }
  }

  if (typeof value !== 'object') {
    return null
  }

  const normalizedAddress = {
    id: normalizePositiveInteger(value.id),
    name: (value.name || '').trim() || null,
    alias: (value.alias || '').trim() || null,
    address: (value.address || '').trim() || null,
    zip: (value.zip || '').trim() || null,
    city: (value.city || '').trim() || null,
  }

  if (!normalizedAddress.id
    && !normalizedAddress.name
    && !normalizedAddress.alias
    && !normalizedAddress.address
    && !normalizedAddress.zip
    && !normalizedAddress.city) {
    return null
  }

  return normalizedAddress
}

const normalizePatientForPayload = patient => {
  const normalized = {
    surname: (patient?.surname || '').trim(),
    name: (patient?.name || '').trim(),
    pesel: (patient?.pesel || '').trim() || null,
    age: normalizePositiveInteger(patient?.age),
    weight: normalizePositiveInteger(patient?.weight),
    sex: patient?.sex || null,
  }

  if (!normalized.surname
    && !normalized.name
    && !normalized.pesel
    && !normalized.age
    && !normalized.weight
    && !normalized.sex) {
    return null
  }

  return normalized
}

const buildOrderRequestPayloadFromForm = form => {
  const requestedType = (form.requestedType || '').trim()
  const eventType = isEventType(requestedType)
  const medicalCourierType = isMedicalCourierType(requestedType)

  const payload = {
    customerId: normalizePositiveInteger(form.customerId),
    requestedType,
    eventKind: eventType ? ((form.eventKind || '').trim() || null) : null,
    requestedStartAt: toApiDatetimeValue(form.requestedStartAt),
    requestedEndAt: eventType ? toApiDatetimeValue(form.requestedEndAt) : null,
    pickup: requiresPickup(requestedType) ? normalizeAddressForPayload(form.pickupAddress) : null,
    delivery: normalizeAddressForPayload(form.deliveryAddress),
    patient: requiresPatient(requestedType) ? normalizePatientForPayload(form.patient) : null,
    notes: form.notes ? form.notes.trim() : null,
    priority: mapPriorityCodeToApiValue(form.priority),
    returnTransport: !medicalCourierType && !eventType && !!form.returnTransport,
    infectious: !medicalCourierType && !eventType && !!form.infectious,
  }

  return payload
}

const validateOrderRequestPayload = payload => {
  const missingFields = []

  if (!payload.customerId) {
    missingFields.push('customerId')
  }
  if (!payload.requestedType) {
    missingFields.push('requestedType')
  }
  if (!payload.requestedStartAt) {
    missingFields.push('requestedStartAt')
  }
  if (!payload.priority) {
    missingFields.push('priority')
  }
  if (requiresPickup(payload.requestedType) && !payload.pickup) {
    missingFields.push('pickup')
  }
  if (!payload.delivery) {
    missingFields.push('delivery')
  }
  if (isEventType(payload.requestedType) && !payload.eventKind) {
    missingFields.push('eventKind')
  }
  if (isEventType(payload.requestedType) && !payload.requestedEndAt) {
    missingFields.push('requestedEndAt')
  }
  if (requiresPatient(payload.requestedType)
    && (!payload.patient?.surname || !payload.patient?.name || !payload.patient?.sex)) {
    missingFields.push('patient')
  }

  return missingFields
}

export {
  createEmptyOrderRequestForm,
  mapOrderRequestToForm,
  buildOrderRequestPayloadFromForm,
  validateOrderRequestPayload,
}
